import React, { useState, useRef, useEffect } from 'react';
import { Card, Input, Button, Space, Tag, Spin, message, Popconfirm, Tabs, Empty, Popover } from 'antd';
import { SendOutlined, UserOutlined, ClearOutlined, DeleteOutlined, BulbOutlined, WechatOutlined, PhoneOutlined, CameraOutlined, CloseOutlined } from '@ant-design/icons';
import { apiFetch } from '../utils/api.js';
import xiaoliAvatar from '../assets/xiaoli-avatar.jpg';
import VoiceCallModal from '../components/VoiceCallModal.jsx';
import ReportDownload from '../components/ReportDownload.jsx';

const SUGGESTIONS = [
  '这个月的账单核算帮我梳理一下，净入账和净利润多少',
  '各产品的利润和毛利率排个名，谁在亏钱',
  '帮我看下公司账本和现金流情况',
  '有哪些订单在亏损？帮我定位一下',
  '存货压了多少资金，有没有负库存',
  '最近几个月财务趋势怎么样，环比是涨是跌',
];

const CATEGORIES = ['账务核算', '成本分析', '利润分析', '资金管理', '经营分析', '税务合规', '风险预警', '其他'];
const CAT_COLORS = {
  '账务核算': 'geekblue', '成本分析': 'cyan', '利润分析': 'gold', '资金管理': 'purple', '经营分析': 'green', '税务合规': 'volcano', '风险预警': 'red', '其他': 'default',
};

// 简单 markdown 渲染：**加粗**、#标题、- 列表
function renderText(text) {
  const lines = String(text || '').split('\n');
  const out = [];
  lines.forEach((line, i) => {
    const t = line.trim();
    if (!t) return;
    if (t.startsWith('### ')) out.push(<div key={i} style={{ fontWeight: 500, fontSize: 14, margin: '8px 0 3px', color: '#8B6914' }}>{t.slice(4)}</div>);
    else if (t.startsWith('## ')) out.push(<div key={i} style={{ fontWeight: 500, fontSize: 15, margin: '8px 0 3px', color: '#8B6914' }}>{t.slice(3)}</div>);
    else if (t.startsWith('# ')) out.push(<div key={i} style={{ fontWeight: 500, fontSize: 16, margin: '8px 0 3px', color: '#8B6914' }}>{t.slice(2)}</div>);
    else if (t.startsWith('- ') || t.startsWith('* ')) out.push(<div key={i} style={{ paddingLeft: 14, position: 'relative', margin: '2px 0' }}><span style={{ position: 'absolute', left: 0 }}>•</span>{inline(t.slice(2))}</div>);
    else out.push(<div key={i} style={{ margin: '2px 0' }}>{inline(t)}</div>);
  });
  return out;
}
function inline(t) {
  const parts = String(t).split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) => i % 2 === 1 ? <b key={i}>{p}</b> : p);
}

// 小李圆形头像（戴眼镜专业会计，统一复用模式）
function LiAvatar({ size = 34, style }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'inline-flex', overflow: 'hidden', border: '1px solid #E8D9B8',
      ...style,
    }}>
      <img src={xiaoliAvatar} alt="小李" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    </span>
  );
}

export default function LiAssistant() {
  const [messages, setMessages] = useState([]);
  const [insights, setInsights] = useState([]);
  const [pushLog, setPushLog] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [catFilter, setCatFilter] = useState('全部');
  const [callOpen, setCallOpen] = useState(false);
  const [imageUrls, setImageUrls] = useState([]); // 待发送的供应商账单照片（多张，已上传到云端的公网 URL，最多 9 张）
  const [imgUploading, setImgUploading] = useState(false);
  const boxRef = useRef(null);
  const fileRef = useRef(null);
  const MAX_IMGS = 9;

  // 加载小李长期记忆（对话 + 财务洞察 + 发微信日志）
  const loadMemory = () => {
    apiFetch('/api/xiaoli/memory').then(r => r.json()).then(d => {
      if (d.data) {
        setMessages(d.data.conversations || []);
        setInsights(d.data.insights || []);
        setPushLog(d.data.pushLog || []);
      }
    }).catch(() => {});
  };
  useEffect(() => { loadMemory(); }, []);

  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [messages, loading]);

  // 选择并上传供应商账单照片（支持多选，逐张 base64 → /api/upload/image → 公网 URL）
  const pickImage = (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    const room = MAX_IMGS - imageUrls.length;
    if (room <= 0) { message.warning('最多上传 ' + MAX_IMGS + ' 张账单照片'); return; }
    const use = list.slice(0, room);
    if (list.length > room) message.warning('最多 ' + MAX_IMGS + ' 张，已只取前 ' + room + ' 张');
    setImgUploading(true);
    const uploadOne = (file) => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const res = await apiFetch('/api/upload/image', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fileBase64: reader.result }),
          }).then(r => r.json());
          resolve(res.success ? res.url : null);
          if (!res.success) message.error(res.error || '其中一张照片上传失败，已跳过');
        } catch (e) { message.error('其中一张照片上传失败，已跳过'); resolve(null); }
      };
      reader.onerror = () => { message.error('其中一张照片读取失败，已跳过'); resolve(null); };
      reader.readAsDataURL(file);
    });
    (async () => {
      const urls = [];
      for (const f of use) { const u = await uploadOne(f); if (u) urls.push(u); }
      if (urls.length) { setImageUrls(prev => [...prev, ...urls].slice(0, MAX_IMGS)); message.success('已上传 ' + urls.length + ' 张账单照片，发送后小李识别并对账'); }
      setImgUploading(false);
    })();
  };

  const send = async (text) => {
    const q = (text || input).trim();
    if ((!q && !imageUrls.length) || loading) return;
    setMessages(prev => [...prev, { role: 'user', content: q || '（供应商送货单照片，共 ' + imageUrls.length + ' 张）', imageUrls }]);
    setInput(''); const sentImgs = imageUrls; setImageUrls([]); setLoading(true);
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), 180000); // 多张识别更耗时，前端 180s 超时兜底
    try {
      const res = await apiFetch('/api/xiaoli/chat', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q, imageUrls: sentImgs.length ? sentImgs : undefined }),
        signal: ac.signal,
      }).then(r => r.json());
      if (res.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: res.reply }]);
        loadMemory(); // 刷新洞察 + 小李可能在对话中发了微信，刷新推送日志
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ ' + (res.error || '小李服务异常，请稍后再试') }]);
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ ' + (e && e.name === 'AbortError' ? '小李思考时间过长（超过 2 分钟），请重试或换个更具体的问题' : '网络错误，请稍后再试') }]);
    } finally {
      clearTimeout(timer);
    }
    setLoading(false);
  };

  const clearMemory = async () => {
    try {
      await apiFetch('/api/xiaoli/clear', { method: 'POST' }).then(r => r.json());
      setMessages([]); setInsights([]); setPushLog([]);
      message.success('小李的记忆已清空');
    } catch (e) { message.error('清空失败'); }
  };

  const delInsight = async (id) => {
    try {
      await apiFetch('/api/xiaoli/insight/' + id, { method: 'DELETE' }).then(r => r.json());
      loadMemory();
      message.success('已删除该条洞察');
    } catch (e) { message.error('删除失败'); }
  };

  const filteredInsights = catFilter === '全部' ? insights : insights.filter(i => i.category === catFilter);

  // ===== 对话气泡 =====
  const chatNode = (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div ref={boxRef} style={{ flex: 1, overflowY: 'auto', padding: '4px 8px' }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '36px 0', color: '#8c8c8c' }}>
            <div style={{ margin: '0 auto 14px' }}>
              <LiAvatar size={56} />
            </div>
            <div style={{ fontSize: 15, color: '#5F5E5A', marginBottom: 6 }}>你好，我是小李。</div>
            <div style={{ fontSize: 13, marginBottom: 18 }}>账务核算、成本利润、资金轧账、经营分析——全公司的账，交给我来算清、看透、预警。</div>
            <Space wrap size={8}>
              {SUGGESTIONS.map(s => (
                <Button key={s} size="small" onClick={() => send(s)} style={{ borderRadius: 14, borderColor: '#E8D9B8', color: '#8B6914' }}>{s}</Button>
              ))}
            </Space>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', marginBottom: 14, justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{ maxWidth: '82%', display: 'flex', gap: 8, flexDirection: m.role === 'user' ? 'row-reverse' : 'row' }}>
              {m.role === 'user' ? (
                <div style={{
                  width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                  background: 'var(--brand)', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14,
                }}>
                  <UserOutlined />
                </div>
              ) : (
                <LiAvatar size={30} />
              )}
              <div style={{
                background: m.role === 'user' ? '#D85A30' : '#fff', color: m.role === 'user' ? '#fff' : '#333',
                borderRadius: 12, padding: '10px 14px', fontSize: 13.5, lineHeight: 1.7,
                boxShadow: m.role === 'user' ? 'none' : '0 1px 4px rgba(0,0,0,0.06)', border: m.role === 'user' ? 'none' : '0.5px solid #E8D9B8',
              }}>
                {m.role === 'user' && (() => {
                  const imgs = m.imageUrls || (m.imageUrl ? [m.imageUrl] : []); // 兼容旧单图历史消息
                  if (!imgs.length) return null;
                  return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: m.content ? 6 : 0, justifyContent: 'flex-end' }}>
                      {imgs.map((u, k) => (
                        <img key={k} src={u} alt={'账单照片' + (k + 1)} style={{ maxWidth: 160, maxHeight: 140, borderRadius: 8, display: 'block', border: '1px solid rgba(255,255,255,0.4)' }} />
                      ))}
                    </div>
                  );
                })()}
                {renderText(m.content)}
                {m.role === 'assistant' && <ReportDownload text={m.content} color="#8B6914" />}
              </div>
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', gap: 8 }}>
            <LiAvatar size={30} />
            <div style={{ background: '#fff', borderRadius: 12, padding: '10px 14px', border: '0.5px solid #E8D9B8' }}><Spin size="small" /> <span style={{ marginLeft: 8, color: '#8c8c8c' }}>小李正在核算、分析账目…</span></div>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 10, borderTop: '0.5px solid #F0E7D2' }}>
        {imageUrls.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {imageUrls.map((u, k) => (
              <div key={k} style={{ position: 'relative', lineHeight: 0 }}>
                <img src={u} alt={'待发送账单' + (k + 1)} style={{ height: 48, width: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #E8D9B8' }} />
                <Button size="small" type="text" icon={<CloseOutlined />}
                  onClick={() => setImageUrls(prev => prev.filter((_, i) => i !== k))}
                  style={{ position: 'absolute', top: -6, right: -6, minWidth: 16, height: 16, padding: 0, background: '#fff', borderRadius: '50%', color: '#bfbfbf', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }} />
              </div>
            ))}
            <span style={{ fontSize: 12, color: '#8c8c8c' }}>已就绪 {imageUrls.length} 张账单照片，发送后小李逐张识别并合并对账（最多 {MAX_IMGS} 张）</span>
            <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => setImageUrls([])} style={{ color: '#bfbfbf' }}>清空</Button>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <Input
            placeholder="跟小李说，例如：帮我梳理下这个月的账，净利润多少"
            value={input}
            onChange={e => setInput(e.target.value)}
            onPressEnter={() => send()}
            allowClear
          />
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => { pickImage(e.target.files); e.target.value = ''; }} />
          <Button icon={<CameraOutlined />} loading={imgUploading} onClick={() => fileRef.current && fileRef.current.click()} title="上传供应商账单照片（可多选），让小李对账" style={{ color: '#8B6914', borderColor: '#E8D9B8' }} />
          <Button icon={<PhoneOutlined />} onClick={() => setCallOpen(true)} title="语音通话" style={{ color: '#8B6914', borderColor: '#E8D9B8' }} />
          <Button type="primary" icon={<SendOutlined />} loading={loading} onClick={() => send()} style={{ background: '#8B6914', borderColor: '#8B6914' }}>发送</Button>
        </div>
      </div>
    </div>
  );

  // ===== 洞察库 =====
  const insightNode = (
    <div style={{ flex: 1, overflowY: 'auto', padding: '4px 4px', minHeight: 0 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12.5, color: '#8c8c8c', marginBottom: 6 }}>按分类筛选</div>
        <Space wrap size={[6, 6]}>
          <Tag.CheckableTag checked={catFilter === '全部'} onChange={() => setCatFilter('全部')} style={{ borderRadius: 12 }}>全部（{insights.length}）</Tag.CheckableTag>
          {CATEGORIES.map(c => (
            <Tag.CheckableTag key={c} checked={catFilter === c} onChange={() => setCatFilter(c)} style={{ borderRadius: 12 }}>
              {c}（{insights.filter(i => i.category === c).length}）
            </Tag.CheckableTag>
          ))}
        </Space>
      </div>
      {filteredInsights.length === 0 ? (
        <Empty description={catFilter === '全部' ? '还没有沉淀的财务洞察，去对话里让小李帮你分析吧' : '这个分类还没有洞察'} style={{ padding: '40px 0' }} />
      ) : (
        filteredInsights.map(i => (
          <div key={i.id} style={{
            border: '1px solid #F0E7D2', background: '#fff', borderRadius: 12, padding: '12px 14px', marginBottom: 10,
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <BulbOutlined style={{ color: '#B8860B', marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: '#26201C' }}>{i.title}</span>
                  <Tag color={CAT_COLORS[i.category] || 'default'} style={{ marginRight: 0 }}>{i.category}</Tag>
                </div>
                <div style={{ fontSize: 13, color: '#5F5E5A', lineHeight: 1.7, marginTop: 6, whiteSpace: 'pre-wrap' }}>{i.content}</div>
                <div style={{ fontSize: 11.5, color: '#bfbfbf', marginTop: 6 }}>{i.ts}</div>
              </div>
              <Popconfirm title="删除这条洞察？" onConfirm={() => delInsight(i.id)} okText="删除" cancelText="取消">
                <Button size="small" type="text" icon={<DeleteOutlined />} style={{ color: '#bfbfbf' }} />
              </Popconfirm>
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="page-container">
      <Card
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LiAvatar size={34} />
            <span>
              <span style={{ fontSize: 16, fontWeight: 500, color: '#26201C' }}>小李</span>
              <span style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 8 }}>财务 AI 员工 · 全量财务核算 / 分析 / 预警</span>
            </span>
          </span>
        }
        extra={
          <Space>
            <Tag color="gold" style={{ marginRight: 0 }}>只分析建议，不改数据</Tag>
            <Popover
              trigger="click"
              title="小李最近发的微信"
              content={
                <div style={{ width: 300, maxHeight: 320, overflowY: 'auto' }}>
                  {pushLog.length === 0 ? (
                    <div style={{ color: '#bfbfbf', fontSize: 12, padding: '8px 0' }}>还没有发过微信记录</div>
                  ) : pushLog.slice(0, 20).map(l => (
                    <div key={l.id} style={{ padding: '7px 0', borderBottom: '0.5px solid #F0E7D2' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {l.ok
                          ? <Tag color="green" style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}>已发</Tag>
                          : <Tag color="red" style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}>失败</Tag>}
                        <span style={{ fontSize: 12.5, color: '#26201C', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.title}</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>{l.ts} · {l.from || '小李主动提醒'}</div>
                      {!l.ok && l.error && <div style={{ fontSize: 11, color: 'var(--brand)', marginTop: 2 }}>{l.error}</div>}
                    </div>
                  ))}
                </div>
              }
            >
              <Button size="small" type="text" icon={<WechatOutlined />} style={{ color: '#07C160' }} />
            </Popover>
            <Popconfirm title="清空小李的全部记忆（对话+洞察）？" onConfirm={clearMemory} okText="清空" cancelText="取消">
              <Button size="small" type="text" icon={<ClearOutlined />} style={{ color: '#8c8c8c' }} />
            </Popconfirm>
          </Space>
        }
        style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}
        bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 12, overflow: 'hidden' }}
      >
        <Tabs
          className="employee-tabs"
          size="small"
          style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          items={[
            { key: 'chat', label: '对话', children: chatNode },
            { key: 'insights', label: `财务洞察（${insights.length}）`, children: insightNode },
          ]}
          tabBarStyle={{ marginBottom: 8 }}
        />
      </Card>

      {/* ===== 语音通话面板（共用 VoiceCallModal，小李金色主题） ===== */}
      <VoiceCallModal
        open={callOpen}
        onClose={() => setCallOpen(false)}
        name="小李"
        avatar={<LiAvatar size={72} />}
        accentColor="#8B6914"
        apiBase="/api/xiaoli"
        onResult={({ recognized, reply }) => {
          setMessages(prev => [...prev,
            { role: 'user', content: '🎤 ' + (recognized || '') },
            { role: 'assistant', content: reply || '' },
          ]);
        }}
      />
    </div>
  );
}